import '../styles/Prerequisites.scss';

import { Stack } from '@whistler-ui/k2/components/Stack';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import { useMemo } from 'react';
import { Link } from 'react-router';

import useRenderIf from '../../../hooks/useRenderIf';
import { useThemeStore } from '../../../store/theme';

const componentName = 'c-Prerequisites';

export function Prerequisites() {
  const { RenderIf } = useRenderIf();

  const { config } = useThemeStore();

  const image = config.assets.onboarding.prerequisites;

  const preRequisitesList = useMemo(() => {
    return [
      {
        content: (
          <Paragraph>
            To identify subscribers, Android devices require a unique
            combination of MCC, MNC, and GID1, while iOS devices use the same
            identifiers but also allow GID2 to be specified. If the MCC and MNC
            alone already uniquely identify your network, the GID values do not
            need to be provided.
          </Paragraph>
        ),
      },
      {
        content: (
          <Paragraph>
            Your own AAA or HSS{' '}
            <RenderIf type="mno">
              or access to your host MNO’s AAA or HSS
            </RenderIf>{' '}
            for{' '}
            <Link to="/developers?tab=eap-aka-%26-dsg">
              EAP-AKA SIM authentication
            </Link>
            .{' '}
          </Paragraph>
        ),
      },
      {
        content: (
          <Paragraph>
            Your own SM-DP+{' '}
            <RenderIf type="mvno">
              or access to your host MNO’s SM-DP+ ES2+
            </RenderIf>{' '}
            to order new eSIMs{' '}
            <Link to="/developers?tab=e-sim">using the target device EID</Link>{' '}
            and release the eSIMs for download. <br />
            <sub>
              Downloading eSIMs using activation (QR) codes is not supported by
              OEMs for ES use cases.
            </sub>
          </Paragraph>
        ),
      },
      {
        content: (
          <Paragraph>
            Your own BSS{' '}
            <RenderIf type="mvno">or access to your host MNO’s BSS</RenderIf> to
            be able to perform a SIM Swap to transfer a subscription to the new
            eSIM.
          </Paragraph>
        ),
      },
    ];
  }, []);

  return (
    <Stack tag="article" direction="row" gap={8} className={componentName}>
      <Stack direction="column" gap={8}>
        <Heading>Prerequisites</Heading>
        <Stack direction="column" gap={3}>
          <Paragraph>
            Before your entitlements solution can be launched, you’ll need all
            of the following in place:
          </Paragraph>
          <Stack tag="ul" direction="column" className="gap-[10px]!">
            {preRequisitesList.map((item, index) => {
              return <li key={`requisite-${index}`}>{item.content}</li>;
            })}
          </Stack>
          <RenderIf ues={true}>
            <Paragraph className="last">
              <b>Important</b>: your network settings, including your ES
              settings, need to be present in{' '}
              <a
                href="https://www.gsma.com/solutions-and-impact/industry-services/device-services/gsma-network-settings-exchange"
                target="_blank"
                rel="noreferrer"
              >
                GSMA NSX
              </a>{' '}
              as this is the mechanism used by the OEM to configure your
              subscriber's devices.
            </Paragraph>
            <Paragraph>
              If you are an MVNO or MVNE and use GID codes to identify your
              subscriber base, please provide the GID code(s) you are currently
              using so we can ensure there are no conflicts with those used by
              your host MNO(s).
            </Paragraph>
          </RenderIf>
        </Stack>
      </Stack>
      {image && (
        <aside>
          <img src={image} alt="prerequisites media" />
        </aside>
      )}
    </Stack>
  );
}
